import React, { useEffect, useState } from 'react'
import {
	AppBar,
	Toolbar,
	Button,
	Box,
	Typography,
	Stack,
	alpha,
	useMediaQuery,
	MenuItem,
	Menu,
	IconButton,
	Divider,
	Avatar,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { motion } from 'framer-motion'
import SearchForm from '../../components/SearchForm'
import { MenuIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useLocation } from 'react-router-dom'

// Styled components with Framer Motion
const HeaderBackground = styled(motion.div)(({ theme }) => ({
	backgroundImage: `linear-gradient(360deg, rgba(0, 0, 0, 0.5) 0%,rgba(207, 147, 28, 0.5) 100%), 
		url(/images/landing.jpg)`,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	// border:'2px solid purple',
	width: '100%',
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	borderRadius: '6px',
	height: '100vh', // Full viewport height by default
	[theme.breakpoints.down('sm')]: {
		height: '95vh', // 50% of viewport height on small screens
	},
	[theme.breakpoints.between('sm', 'md')]: {
		height: '80vh', // 60% of viewport height on medium-small screens
	},
	[theme.breakpoints.between('md', 'lg')]: {
		height: '80vh', // 70% of viewport height on medium screens
		maxHeight: '860px', // Cap the maximum height
	},
	[theme.breakpoints.between('lg', 'xl')]: {
		height: '80vh', // 80% of viewport height on large screens
		maxHeight: '860px', // Cap the maximum height
	},
	[theme.breakpoints.up('xl')]: {
		height: '90vh', // 90% of viewport height on extra-large screens
		maxHeight: '860px', // Cap the maximum height
	},
}))

const StyledAppBar = styled(AppBar)(({ theme }) => ({
	background: alpha(theme.palette.background.paper, 0.7),
	backdropFilter: 'blur(10px)',
	borderTopRightRadius: '6px',
	borderTopLeftRadius: '6px',
	transition: 'border-radius 0.3s ease',
}))

const Logo = styled(motion.img)(({ theme }) => ({
	height: '48px', // Default height
	cursor: 'pointer',
	[theme.breakpoints.down('sm')]: {
		height: '28px', // Smaller height for mobile screens
	},
}))

const ProviderLogo = styled(motion.img)(({ theme }) => ({
	height: '36px', // Default height
	cursor: 'pointer',
	[theme.breakpoints.down('sm')]: {
		height: '18px', // Smaller height for mobile screens
	},
	[theme.breakpoints.down('sm')]: {
		height: '24px', // Smaller height for mobile screens
	},
	[theme.breakpoints.up('md')]: {
		height: '36px', // Larger height for medium screens and above
	},
	[theme.breakpoints.up('lg')]: {
		height: '36px', // Even larger for large screens
	},
}))

const VisaLogo = styled(motion.img)(({ theme }) => ({
	height: '36px', // Default height
	cursor: 'pointer',
	[theme.breakpoints.down('xs')]: {
		height: '16px', // Smaller height for mobile screens
	},
	[theme.breakpoints.down('sm')]: {
		height: '20px', // Smaller height for mobile screens
	},
	[theme.breakpoints.up('md')]: {
		height: '32px', // Larger height for medium screens and above
	},
	[theme.breakpoints.up('lg')]: {
		height: '32px', // Even larger for large screens
	},
}))

const MobitillLogo = styled(motion.img)(({ theme }) => ({
	height: '36px', // Default height
	cursor: 'pointer',
	[theme.breakpoints.down('xs')]: {
		height: '18px', // Smaller height for mobile screens
	},
	[theme.breakpoints.down('sm')]: {
		height: '24px', // Smaller height for mobile screens
	},
	[theme.breakpoints.up('md')]: {
		height: '36px', // Larger height for medium screens and above
	},
	[theme.breakpoints.up('lg')]: {
		height: '36px', // Even larger for large screens
	},
}))

const ContentBox = styled(motion.div)(({ theme }) => ({
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	textAlign: 'center',
	width: '100%',
	padding: theme.spacing(2),
}))

const MainHeading = styled(motion(Typography))(({ theme }) => ({
	color: '#fff',
	fontWeight: 'bold',
	textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
	marginBottom: theme.spacing(1),
	[theme.breakpoints.down('xs')]: {
		fontSize: '12px',
		marginTop: theme.spacing(3),
	},
	[theme.breakpoints.down('sm')]: {
		fontSize: '24px',
		marginTop: theme.spacing(6),
	},
	[theme.breakpoints.between('sm', 'md')]: {
		fontSize: '48px',
		marginTop: theme.spacing(3),
	},
	[theme.breakpoints.between('md', 'lg')]: {
		fontSize: '56px',
		marginTop: theme.spacing(3),
	},
	[theme.breakpoints.between('lg', 'xl')]: {
		fontSize: '60px',
		marginTop: theme.spacing(3),
	},
	[theme.breakpoints.up('xl')]: {
		fontSize: '72px',
	},
}))

const SubHeading = styled(motion(Typography))(({ theme }) => ({
	color: '#fff',
	textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
	[theme.breakpoints.down('sm')]: {
		fontSize: '16px', // 50% of viewport height on small screens
		marginBottom: theme.spacing(1),
	},
	[theme.breakpoints.between('sm', 'md')]: {
		fontSize: '24px', // 60% of viewport height on medium-small screens
		marginBottom: theme.spacing(2),
	},
	[theme.breakpoints.between('md', 'lg')]: {
		fontSize: '30px', // 70% of viewport height on medium screens
		marginBottom: theme.spacing(4),
	},
	[theme.breakpoints.between('lg', 'xl')]: {
		fontSize: '36px', // 80% of viewport height on large screens
		marginBottom: theme.spacing(4),
	},
	[theme.breakpoints.up('xl')]: {
		fontSize: '40px', // 90% of viewport height on extra-large screens
		marginBottom: theme.spacing(4),
	},
}))

const AnimatedButton = motion(Button)

const Header = () => {
	const [anchorEl, setAnchorEl] = useState(null)
	const [userAnchorEl, setUserAnchorEl] = useState(null);
	const [noBorderRadius, setNoBorderRadius] = useState(false)
	const {currentUser, logout} = useAuth() || {currentUser: null, logout: () => {}}
	const navigate = useNavigate()

	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

	
	
	 
	
	  const handleUserMenu = (event) => {
		setUserAnchorEl(event.currentTarget);
	  };
	
	  const handleUserMenuClose = () => {
		setUserAnchorEl(null);
	  };

	async function handleLogout() {
		try {
		  await logout();
		  handleUserMenuClose();
		  navigate('/');
		} catch (error) {
		  console.error('Logout failed', error);
		}
	  }

	const handleMenu = event => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}
	// Animation variants
	const fadeIn = {
		initial: { opacity: 0, y: 20 },
		animate: { opacity: 1, y: 0 },
		transition: { duration: 0.6 },
	}

	const headerVariants = {
		initial: { opacity: 0 },
		animate: {
			opacity: 1,
			transition: {
				duration: 1.2,
			},
		},
	}

	const logoVariants = {
		initial: { opacity: 0, x: -20 },
		animate: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 0.6,
			},
		},
	}

	const buttonVariants = {
		hover: {
			scale: 1.05,
			transition: { duration: 0.2 },
		},
		tap: {
			scale: 0.95,
		},
	}

	const textVariants = {
		initial: { opacity: 0, y: 30 },
		animate: i => ({
			opacity: 1,
			y: 0,
			transition: {
				delay: i * 0.2,
				duration: 0.8,
				ease: 'easeOut',
			},
		}),
	}

	// useEffect(() => {
	// 	const handleScroll = () => {
	// 		setNoBorderRadius(window.scrollY > 16)
	// 	}

	// 	window.addEventListener('scroll', handleScroll)
	// 	return () => window.removeEventListener('scroll', handleScroll)
	// }, [])

	return (
		<HeaderBackground
			initial="initial"
			animate="animate"
			variants={headerVariants}
		>
			<StyledAppBar
				position="sticky"
				elevation={0}
				style={{ borderTopRightRadius: noBorderRadius ? '0px' : '6px', borderTopLeftRadius: noBorderRadius ? '0px' : '6px' }}
			>
				<Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Logo
						src="/images/epesiPrimary.png"
						alt="logo"
						variants={logoVariants}
						onClick={() => navigate('/')}
						style={{ cursor: 'pointer' }}
					/>

					
				</Toolbar>
			</StyledAppBar>

			<ContentBox>
				<MainHeading custom={0} variants={textVariants}>
					Safiri bila stress!
					<br />
					Tumekusort!
				</MainHeading>
				<SubHeading variant="h5" custom={1} variants={textVariants}>
					Long distance travel simplified
				</SubHeading>

				<motion.div
					initial="initial"
					animate="animate"
					variants={fadeIn}
					style={{ maxWidth: 1200, margin: '0 auto' }}
				>
					<SearchForm />
				</motion.div>
			</ContentBox>
			<Box
				sx={{
					minWidth: { xs: '280px', sm: '320px' }, // Minimum width for mobile screens
					height: '80px',
					position: 'absolute',
					backgroundColor: 'white',
					bottom: { xs: '-100px', sm: '-40px' },
					margin: '0 auto',
					display: 'flex',
					flexDirection: { xs: 'row', sm: 'row' },
					flexWrap: { xs: 'wrap' },
					alignItems: 'center',
					justifyContent: 'space-around', // Adjust spacing for smaller screens
					gap: '0', // Reduce gap for mobile screens
					padding: { xs: '0 8px', sm: '0 16px' }, // Adjust padding for smaller screens
					borderRadius: '6px',
					boxShadow: '0px 1px 10.4px -2px rgba(0, 0, 0, 0.25)',
					width: {
						xs: '95%', // Use most of the screen width on extra-small screens
						sm: '90%', // Slightly smaller width for small screens
						md: '70%',
						lg: '50%',
						xl: '70%',
					},
				}}
			>
				<ProviderLogo
					src="/images/mpesa.svg"
					alt="logo"
					variants={logoVariants}
				/>
				<ProviderLogo
					src="/images/airtel-logo-red-text-horizontal.jpg"
					alt="logo"
					variants={logoVariants}
				/>
				<Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
					<VisaLogo src="/images/visa.svg" alt="logo" variants={logoVariants} />
					<Divider orientation="vertical" flexItem />
					<ProviderLogo
						src="/images/mastercard.svg"
						alt="logo"
						variants={logoVariants}
					/>
				</Box>
				<MobitillLogo
					src="/images/mobitillLogoNew.png"
					alt="logo"
					variants={logoVariants}
				/>
			</Box>
		</HeaderBackground>
	)
}

export default Header
